import { getCustomerAccessForFrontendUi } from "@/app/auth/frontendUiAuthSession";
import { CUSTOMER_AUTH_SESSION_EVENT } from "@/app/services/authApi";
import type { ActivityPointer, AppointmentActivityItem, WaitlistActivityItem } from "./activityContracts";
import { isActivityIdentifier, validateActivityItem } from "./activityValidation";

export const FRONTEND_BOOKING_ACTIVITY_PREVIEW_STORAGE_KEY = "volera.frontendBookingActivityPreview.v1";
export const FRONTEND_BOOKING_ACTIVITY_PREVIEW_LIMIT = 20;
const PREVIEW_EVENT = "volera:frontend-booking-activity-preview";
const MAX_STORAGE_LENGTH = 512_000;
type BookingActivityPreview = AppointmentActivityItem | WaitlistActivityItem;
const EMPTY: readonly BookingActivityPreview[] = Object.freeze([]);

function currentOwner(): string | null {
  if (process.env.NODE_ENV !== "development" || typeof window === "undefined") return null;
  try {
    const owner = getCustomerAccessForFrontendUi()?.account.uuid;
    return isActivityIdentifier(owner) ? owner : null;
  } catch { return null; }
}

function validatePreview(input: unknown, owner: string): BookingActivityPreview | null {
  const result = validateActivityItem(input);
  if (!result.valid || (result.item.activityType !== "appointment" && result.item.activityType !== "waitlist")) return null;
  const item = result.item;
  const details = item.capabilities[0];
  if ((item.activityType === "appointment" && !item.activityId.startsWith("frontend-booking-preview-")) ||
    item.ownership.customerAccountId !== owner ||
    item.capabilities.length !== 1 || details?.action !== "open-details" ||
    details.availability !== "allowed" || details.reason !== null ||
    details.requiresFreshData || details.requiresCompleteData ||
    details.destination?.kind !== "dialog" || details.destination.dialog !== "activity-details" ||
    details.destination.providerActionId !== null || details.destination.providerActionRevision !== null ||
    (item.activityType === "appointment" && (item.forms.length > 0 || item.formsPublicationV1 || item.membershipRedemption ||
    item.packageRedemption || item.giftCardRedemption)) ||
    (item.activityType === "waitlist" && (item.status !== "active" || item.offeredSlot.status !== "none")) ||
    item.cancellationPolicy.availability !== "unavailable" || item.refund.status !== "none") return null;
  return item;
}

/** Read-only development presentation, never a current booking publication. */
export function readFrontendBookingActivityPreviews(customerAccountId: string): readonly BookingActivityPreview[] {
  if (currentOwner() !== customerAccountId) return EMPTY;
  try {
    const raw = window.sessionStorage.getItem(FRONTEND_BOOKING_ACTIVITY_PREVIEW_STORAGE_KEY);
    if (!raw || raw.length > MAX_STORAGE_LENGTH) return EMPTY;
    const stored: unknown = JSON.parse(raw);
    if (!stored || typeof stored !== "object" || Array.isArray(stored)) return EMPTY;
    const envelope = stored as Record<string, unknown>;
    if (Object.keys(envelope).length !== 3 || envelope.schemaVersion !== 1 ||
      envelope.customerAccountId !== customerAccountId || !Array.isArray(envelope.items) ||
      envelope.items.length > FRONTEND_BOOKING_ACTIVITY_PREVIEW_LIMIT) return EMPTY;
    const items: BookingActivityPreview[] = [];
    const pointers = new Set<ActivityPointer>();
    for (const candidate of envelope.items) {
      const item = validatePreview(candidate, customerAccountId);
      if (!item || pointers.has(item.pointer)) return EMPTY;
      pointers.add(item.pointer);
      items.push(item);
    }
    return currentOwner() === customerAccountId ? Object.freeze(items) : EMPTY;
  } catch { return EMPTY; }
}

/** Stores only the supplied frontend presentation; does not submit or accept a booking. */
export function registerFrontendBookingActivityPreview(item: BookingActivityPreview): ActivityPointer | null {
  const owner = currentOwner();
  if (!owner) return null;
  const validated = validatePreview(item, owner);
  if (!validated) return null;
  const previous = readFrontendBookingActivityPreviews(owner);
  const items = [...previous.filter(row => row.pointer !== validated.pointer), validated]
    .slice(-FRONTEND_BOOKING_ACTIVITY_PREVIEW_LIMIT);
  try {
    const raw = JSON.stringify({ schemaVersion: 1, customerAccountId: owner, items });
    if (raw.length > MAX_STORAGE_LENGTH || currentOwner() !== owner) return null;
    window.sessionStorage.setItem(FRONTEND_BOOKING_ACTIVITY_PREVIEW_STORAGE_KEY, raw);
    if (currentOwner() !== owner || window.sessionStorage.getItem(FRONTEND_BOOKING_ACTIVITY_PREVIEW_STORAGE_KEY) !== raw) return null;
    window.dispatchEvent(new Event(PREVIEW_EVENT));
    return validated.pointer;
  } catch { return null; }
}

/** Invalidation only: callers must reread and validate the active owner. */
export function subscribeFrontendBookingActivityPreviews(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const onStorage = (event: StorageEvent) => {
    if (event.key === null || event.key === FRONTEND_BOOKING_ACTIVITY_PREVIEW_STORAGE_KEY) listener();
  };
  window.addEventListener(PREVIEW_EVENT, listener);
  window.addEventListener(CUSTOMER_AUTH_SESSION_EVENT, listener);
  window.addEventListener("storage", onStorage);
  return () => {
    window.removeEventListener(PREVIEW_EVENT, listener);
    window.removeEventListener(CUSTOMER_AUTH_SESSION_EVENT, listener);
    window.removeEventListener("storage", onStorage);
  };
}
